import React, { useState, useEffect } from 'react';
import {
    CheckCircleIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon,
    ArrowRightIcon,
    ArrowLeftIcon,
    XMarkIcon,
    KeyIcon,
    RocketLaunchIcon,
    CogIcon,
    EyeIcon,
    EyeSlashIcon,
    ClipboardDocumentIcon
} from '@heroicons/react/24/outline';

// Main Onboarding Tutorial Component
const OnboardingTutorial = ({ isOpen, onClose, onComplete }) => {
    // console.log('OnboardingTutorial props:', { isOpen, onClose, onComplete }); // Add this line
    const [currentStep, setCurrentStep] = useState(0);
    const [completedSteps, setCompletedSteps] = useState(new Set());
    const [credentials, setCredentials] = useState({});
    const [showApiKey, setShowApiKey] = useState({});
    const [credentialsLoaded, setCredentialsLoaded] = useState(false);

    useEffect(() => {
        if (isOpen) {
            fetchExistingCredentials();
            checkExistingProgress();
        }
    }, [isOpen]);

    useEffect(() => {
        // console.log('🔍 Credentials state updated:', credentials);
        // console.log('🔍 Has OpenAI in state:', !!credentials.OPENAI_API_KEY);
        // console.log('🔍 Has Gemini in state:', !!credentials.GEMINI_API_KEY);
    }, [credentials]);

    const fetchExistingCredentials = async () => {
        try {
            const response = await fetch('/api/settings/credentials', {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                const existingCredentials = data.data || {};

                setCredentials(existingCredentials);
                setCredentialsLoaded(true);
            }
            else {
                setCredentialsLoaded(true); // Also set true on error to continue
            }
        } catch (error) {
            console.error('Failed to fetch existing credentials:', error);
            // Continue with empty credentials if fetch fails
        }

        if (!credentialsLoaded) {
            return (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen">
                        <div className="bg-white rounded-lg p-6 shadow-xl">
                            <div className="text-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                                <p>Loading your settings...</p>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

    };

    const checkExistingProgress = async () => {
        try {
            const response = await fetch('/api/user/setup-status', {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                const setupData = data.data;

                // Build completed steps based on what's actually configured
                const newCompletedSteps = new Set(['welcome']); // Welcome is always completed

                if (setupData.openaiConfigured) {
                    newCompletedSteps.add('openai-setup');
                }
                if (setupData.geminiConfigured) {
                    newCompletedSteps.add('gemini-setup');
                }
                if (setupData.platformsConfigured) {
                    newCompletedSteps.add('social-platforms');
                }

                setCompletedSteps(newCompletedSteps);

                // Find the first incomplete required step or move to tutorial overview
                const firstIncompleteStep = findFirstIncompleteStep(newCompletedSteps);
                setCurrentStep(firstIncompleteStep);

            }
        } catch (error) {
            console.error('Failed to check setup progress:', error);
            // Start from beginning on error
            setCurrentStep(0);
            setCompletedSteps(new Set());
        }
    };

    const goToNextLogicalStep = () => {
        // Find the next step that makes sense
        const nextStep = findNextLogicalStep();
        if (nextStep !== -1) {
            setCurrentStep(nextStep);
        }
    };

    const findNextLogicalStep = () => {
        // If OpenAI isn't complete, go there
        if (!completedSteps.has('openai-setup')) {
            return steps.findIndex(step => step.id === 'openai-setup');
        }

        // If we have basic setup, skip to tutorial overview
        if (completedSteps.has('openai-setup') && currentStep < 4) {
            return steps.findIndex(step => step.id === 'tutorial-overview');
        }

        // Normal progression
        return currentStep < steps.length - 1 ? currentStep + 1 : -1;
    };

    const findFirstIncompleteStep = (completedSteps) => {
        // If OpenAI isn't set up, start there (required)
        if (!completedSteps.has('gemini-setup')) {
            return 1; // Gemini setup step
        }

        // If we have the essentials, go to tutorial overview
        if (completedSteps.has('gemini-setup')) {
            return 4; // Tutorial overview step
        }

        // Default to welcome step
        return 0;
    };

    const steps = [
        {
            id: 'welcome',
            title: 'Welcome to PostMgr!',
            description: 'Let\'s get you set up in just a few minutes',
            component: 'WelcomeStep'
        },
        {
            id: 'gemini-setup',
            title: 'Google Gemini Setup',
            description: 'Add Gemini for enhanced AI capabilities',
            component: 'GeminiStep',
            required: true
        },
        {
            id: 'openai-setup',
            title: 'OpenAI API Setup',
            description: 'Configure OpenAI for AI-powered content generation',
            component: 'OpenAIStep',
            required: false
        },

        {
            id: 'social-platforms',
            title: 'Social Media Platforms',
            description: 'Connect your social media accounts',
            component: 'SocialPlatformsStep',
            required: false
        },
        {
            id: 'tutorial-overview',
            title: 'How to Use PostMgr',
            description: 'Learn the basics of generating and managing posts',
            component: 'TutorialStep'
        },
        {
            id: 'complete',
            title: 'Setup Complete!',
            description: 'You\'re ready to start creating amazing content',
            component: 'CompleteStep'
        }
    ];

    const handleNext = () => {
        if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handlePrev = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleStepComplete = (stepId) => {
        setCompletedSteps(prev => new Set([...prev, stepId]));
    };

    const handleCredentialSave = async (key, value) => {
        // Update local state immediately for UI responsiveness
        setCredentials(prev => ({ ...prev, [key]: value }));

        // Save to backend if value is not empty
        if (value && value.trim()) {
            try {
                const response = await fetch('/api/settings/credentials', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
                    },
                    body: JSON.stringify({ [key]: value.trim() })
                });

                if (!response.ok) {
                    console.error('Failed to save credential:', key);
                    // Could add toast notification here if you want
                }
            } catch (error) {
                console.error('Error saving credential:', error);
            }
        }
    };

    const isStepComplete = (stepId) => completedSteps.has(stepId);
    const currentStepData = steps[currentStep];

    const renderStepContent = () => {
        switch (currentStepData.component) {
            case 'WelcomeStep':
                return <WelcomeStep onComplete={() => handleStepComplete('welcome')} />;
            case 'OpenAIStep':
                return (
                    <OpenAIStep
                        onComplete={() => handleStepComplete('openai-setup')}
                        onCredentialSave={handleCredentialSave}
                        showApiKey={showApiKey}
                        setShowApiKey={setShowApiKey}
                        existingCredentials={credentials} // Add this line
                    />
                );
            case 'GeminiStep':
                return (
                    <GeminiStep
                        onComplete={() => handleStepComplete('gemini-setup')}
                        onCredentialSave={handleCredentialSave}
                        showApiKey={showApiKey}
                        setShowApiKey={setShowApiKey}
                        existingCredentials={credentials} // Add this line
                    />
                );
            case 'SocialPlatformsStep':
                return <SocialPlatformsStep onComplete={() => handleStepComplete('social-platforms')} />;
            case 'TutorialStep':
                return <TutorialOverviewStep onComplete={() => handleStepComplete('tutorial-overview')} />;
            case 'CompleteStep':
                return <CompleteStep onComplete={onComplete} credentials={credentials} />;
            default:
                return null;
        }
    };

    if (!isOpen) {
        // console.log('OnboardingTutorial not rendering because isOpen is:', isOpen);
        return null;
    }
    // console.log('OnboardingTutorial SHOULD be rendering, isOpen is:', isOpen);

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
                    {/* Header */}
                    <div className="bg-blue-50 px-6 py-4 border-b border-blue-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <h1 className="text-xl font-semibold text-blue-900">
                                    {currentStepData.title}
                                </h1>
                                <p className="text-sm text-blue-700 mt-1">
                                    {currentStepData.description}
                                </p>
                            </div>
                            <button
                                onClick={onClose}
                                className="text-blue-400 hover:text-blue-600"
                            >
                                <XMarkIcon className="h-6 w-6" />
                            </button>
                        </div>

                        {/* Progress Bar */}
                        <div className="mt-4">
                            <div className="flex items-center space-x-2">
                                {steps.map((step, index) => (
                                    <div key={step.id} className="flex items-center">
                                        <div className={`
                      w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium
                      ${index === currentStep
                                            ? 'bg-blue-600 text-white'
                                            : isStepComplete(step.id)
                                                ? 'bg-green-500 text-white'
                                                : 'bg-gray-300 text-gray-600'
                                        }
                    `}>
                                            {isStepComplete(step.id) ? (
                                                <CheckCircleIcon className="h-5 w-5" />
                                            ) : (
                                                index + 1
                                            )}
                                        </div>
                                        {index < steps.length - 1 && (
                                            <div className={`h-1 w-8 mx-2 ${
                                                isStepComplete(step.id) ? 'bg-green-500' : 'bg-gray-300'
                                            }`} />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="px-6 py-6 min-h-[400px]">
                        {renderStepContent()}
                    </div>

                    {/* Footer */}
                    <div className="bg-gray-50 px-6 py-4 flex justify-between">
                        <button
                            onClick={handlePrev}
                            disabled={currentStep === 0}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <ArrowLeftIcon className="h-4 w-4 mr-2" />
                            Previous
                        </button>

                        <div className="text-sm text-gray-500 flex items-center">
                            Step {currentStep + 1} of {steps.length}
                        </div>

                        <button
                            onClick={goToNextLogicalStep}
                            disabled={currentStep === steps.length - 1}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Next
                            <ArrowRightIcon className="h-4 w-4 ml-2" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Individual Step Components
const WelcomeStep = ({ onComplete }) => {
    useEffect(() => {
        onComplete();
    }, []);

    return (
        <div className="text-center space-y-6">
            <div className="mx-auto w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center">
                <RocketLaunchIcon className="h-12 w-12 text-blue-600" />
            </div>

            <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    Welcome to PostMgr!
                </h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                    PostMgr helps you create and manage social media content using AI.
                    This quick setup will get you ready to generate amazing posts in minutes.
                </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-2">What we'll set up:</h3>
                <ul className="text-sm text-blue-800 space-y-1">
                    <li>• AI services for content generation (OpenAI, Gemini)</li>
                    <li>• Social media platform connections</li>
                    <li>• Basic tutorial on using the platform</li>
                </ul>
            </div>
        </div>
    );
};

const OpenAIStep = ({ onComplete, onCredentialSave, showApiKey, setShowApiKey, existingCredentials  }) => {
    const [apiKey, setApiKey] = useState(existingCredentials?.OPENAI_API_KEY || '');
    const [isValidating, setIsValidating] = useState(false);
    const [validationResult, setValidationResult] = useState(null);

    useEffect(() => {
        if (existingCredentials.OPENAI_API_KEY && !apiKey) {
            setApiKey(existingCredentials.OPENAI_API_KEY);
            setValidationResult({
                valid: true,
                message: 'Using existing API key from your settings!'
            });
        }
    }, [existingCredentials.OPENAI_API_KEY]);

    const handleApiKeyChange = (value) => {
        setApiKey(value);
        setValidationResult(null);

        // Save immediately when changed
        onCredentialSave('OPENAI_API_KEY', value);

        // Real-time validation
        if (value.trim()) {
            if (!value.startsWith('sk-')) {
                setValidationResult({
                    valid: false,
                    message: 'OpenAI API keys must start with "sk-"'
                });
            } else if (value.length < 45) {
                setValidationResult({
                    valid: false,
                    message: 'OpenAI API keys are typically 51 characters long'
                });
            } else {
                setValidationResult({
                    valid: true,
                    message: 'API key format looks correct!'
                });
                // Auto-complete step if valid key is entered
                onComplete();
            }
        }
    };

    const validateApiKey = async () => {
        if (!apiKey.trim()) return;

        setIsValidating(true);
        try {
            // Simulate API validation
            await new Promise(resolve => setTimeout(resolve, 1000));
            setValidationResult({ valid: true, message: 'API key is valid!' });
            onComplete();
        } catch (error) {
            setValidationResult({ valid: false, message: 'Invalid API key. Please check and try again.' });
        } finally {
            setIsValidating(false);
        }
    };

    const copyExample = () => {
        navigator.clipboard.writeText('sk-...');
    };

    return (
        <div className="space-y-6">
            <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                    <KeyIcon className="h-8 w-8 text-blue-600" />
                </div>
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        OpenAI API Key Setup (Optional)
                    </h3>
                    <p className="text-gray-600">
                        OpenAI powers our advanced AI content generation.
                    </p>
                </div>
            </div>

            <div className="space-y-4">
                <div>
                    <h4 className="font-medium text-gray-900 mb-3">How to get your OpenAI API key:</h4>
                    <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600">
                        <li>Go to <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">platform.openai.com/api-keys</a></li>
                        <li>Sign in or create an OpenAI account</li>
                        <li>Click "Create new secret key"</li>
                        <li>Copy the key (it starts with "sk-")</li>
                        <li>Paste it in the field below</li>
                    </ol>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        OpenAI API Key
                    </label>
                    <div className="relative">
                        <input
                            type={showApiKey.openai ? "text" : "password"}
                            value={apiKey}
                            onChange={(e) => handleApiKeyChange(e.target.value)}
                            placeholder="sk-..."
                            className="w-full px-3 py-2 pr-20 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <div className="absolute inset-y-0 right-0 flex items-center space-x-1 pr-3">
                            <button
                                type="button"
                                onClick={() => setShowApiKey(prev => ({ ...prev, openai: !prev.openai }))}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                {showApiKey.openai ? (
                                    <EyeSlashIcon className="h-4 w-4" />
                                ) : (
                                    <EyeIcon className="h-4 w-4" />
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={copyExample}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <ClipboardDocumentIcon className="h-4 w-4" />
                            </button>

                        </div>

                    </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                    {apiKey.trim() && (
                        <button
                            onClick={validateApiKey}
                            disabled={isValidating}
                            className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                        >
                            {isValidating ? 'Validating...' : 'Test API Key'}
                        </button>
                    )}
                    <button
                        onClick={async () => {
                            await onCredentialSave('OPENAI_API_KEY', apiKey);
                            onComplete();
                        }}
                        className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                    >
                        Save & Continue
                    </button>
                </div>

                {validationResult && (
                    <div className={`p-3 rounded-md ${
                        validationResult.valid
                            ? 'bg-green-50 border border-green-200 text-green-800'
                            : 'bg-red-50 border border-red-200 text-red-800'
                    }`}>
                        {validationResult.message}
                    </div>
                )}
            </div>
        </div>
    );
};

const GeminiStep = ({ onComplete, onCredentialSave, showApiKey, setShowApiKey, existingCredentials = {} }) => {
    const [apiKey, setApiKey] = useState(existingCredentials?.GEMINI_API_KEY || '');

    useEffect(() => {

        const existingKey = existingCredentials?.GEMINI_API_KEY;

        if (existingKey && existingKey !== apiKey) {
            setApiKey(existingKey);
        }
    }, [existingCredentials?.GEMINI_API_KEY, apiKey]);

    const handleApiKeyChange = (value) => {
        setApiKey(value);
        onCredentialSave('GEMINI_API_KEY', value);
    };

    const handleSkip = () => {
        onComplete();
    };

    return (
        <div className="space-y-6">
            <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                    <KeyIcon className="h-8 w-8 text-purple-600" />
                </div>
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Google Gemini API Key (Required but FREE)
                    </h3>
                    <p className="text-gray-600">
                        Gemini provides AI capabilities and image and text generation features.
                    </p>
                </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex">
                    <InformationCircleIcon className="h-5 w-5 text-blue-400 mr-2 mt-0.5" />
                    <div>
                        <h4 className="font-medium text-blue-800">Optional but Recommended</h4>
                        <p className="text-sm text-blue-700 mt-1">
                            Gemini enhances your content generation with additional AI models and Whisk image generation.
                        </p>
                    </div>
                </div>
            </div>

            <div className="space-y-4">
                <div>
                    <h4 className="font-medium text-gray-900 mb-3">How to get your Gemini API key:</h4>
                    <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600">
                        <li>Go to <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google AI Studio</a></li>
                        <li>Sign in with your Google account</li>
                        <li>Click "Create API key"</li>
                        <li>Copy the generated key</li>
                        <li>Paste it in the field below</li>
                    </ol>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Gemini API Key
                    </label>
                    <div className="relative">
                        <input
                            type={showApiKey.gemini ? "text" : "password"}
                            value={apiKey}
                            onChange={(e) => handleApiKeyChange(e.target.value)}
                            placeholder="AIza..."
                            className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        />
                        <button
                            type="button"
                            onClick={() => setShowApiKey(prev => ({ ...prev, gemini: !prev.gemini }))}
                            className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                        >
                            {showApiKey.gemini ? (
                                <EyeSlashIcon className="h-4 w-4" />
                            ) : (
                                <EyeIcon className="h-4 w-4" />
                            )}
                        </button>
                    </div>
                </div>

                {existingCredentials?.GEMINI_API_KEY && apiKey && (
                    <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-md">
                        <div className="flex items-center">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" />
                            <span className="text-sm text-green-700">
                    ✅ Found existing API key in your settings
                </span>
                        </div>
                    </div>
                )}

                <div className="flex space-x-3">
                    <button
                        onClick={async () => {
                            await onCredentialSave('GEMINI_API_KEY', apiKey);
                            onComplete();
                        }}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700"
                    >
                        Save & Continue
                    </button>
                    <button
                        onClick={handleSkip}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                        Skip for Now
                    </button>
                </div>
            </div>
        </div>
    );
};

const SocialPlatformsStep = ({ onComplete }) => {
    const platforms = [
        { name: 'LinkedIn', icon: '💼', description: 'Connect your LinkedIn profile and company pages' },
        { name: 'Twitter/X', icon: '🐦', description: 'Post to Twitter/X automatically' },
        { name: 'BlueSky', icon: '🦋', description: 'Share content on BlueSky' },
        { name: 'Instagram', icon: '📷', description: 'Connect Instagram business account (coming soon)' }
    ];

    useEffect(() => {
        onComplete();
    }, []);

    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Social Media Platforms
                </h3>
                <p className="text-gray-600">
                    Connect your social media accounts to automatically publish your AI-generated content.
                </p>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex">
                    <InformationCircleIcon className="h-5 w-5 text-green-400 mr-2 mt-0.5" />
                    <div>
                        <h4 className="font-medium text-green-800">Set Up Later</h4>
                        <p className="text-sm text-green-700 mt-1">
                            You can connect these platforms later in Settings → Platforms. For now, let's focus on getting your AI setup working.
                        </p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {platforms.map((platform) => (
                    <div key={platform.name} className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
                        <div className="flex items-center space-x-3">
                            <span className="text-2xl">{platform.icon}</span>
                            <div>
                                <h4 className="font-medium text-gray-900">{platform.name}</h4>
                                <p className="text-sm text-gray-600">{platform.description}</p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="text-center">
                <p className="text-sm text-gray-500">
                    👆 These can be configured later in Settings → LinkedIn Wizard / Platform Settings
                </p>
            </div>
        </div>
    );
};

const TutorialOverviewStep = ({ onComplete }) => {
    const features = [
        {
            title: '🎯 Generate Posts',
            description: 'Click "Generate Post" on the dashboard to create AI-powered content',
            steps: ['Click the Generate button', 'AI analyzes your content sources', 'Review and customize the generated post', 'Publish or schedule']
        },
        {
            title: '📝 Content Sources',
            description: 'Add websites and RSS feeds for content inspiration',
            steps: ['Go to Settings → Content Sources', 'Add your favorite websites/blogs', 'PostMgr will use these for inspiration', 'AI creates original content based on trending topics']
        },
        {
            title: '⚙️ Platform Settings',
            description: 'Configure which platforms to post to and how',
            steps: ['Visit Settings → Platforms', 'Enable your preferred social networks', 'Set posting preferences', 'Configure approval workflows']
        },
        {
            title: '📊 Review & Manage',
            description: 'Track all your generated content in one place',
            steps: ['View all posts in the Posts section', 'See posting status and engagement', 'Edit or repost content', 'Analyze performance over time']
        }
    ];

    useEffect(() => {
        onComplete();
    }, []);

    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    How to Use PostMgr
                </h3>
                <p className="text-gray-600">
                    Here's a quick overview of PostMgr's main features and how to use them effectively.
                </p>
            </div>

            <div className="space-y-6">
                {features.map((feature, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 mb-2">{feature.title}</h4>
                        <p className="text-sm text-gray-600 mb-3">{feature.description}</p>
                        <ol className="list-decimal list-inside space-y-1">
                            {feature.steps.map((step, stepIndex) => (
                                <li key={stepIndex} className="text-sm text-gray-700">{step}</li>
                            ))}
                        </ol>
                    </div>
                ))}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-800 mb-2">💡 Pro Tips</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Start with test mode enabled to review posts before they go live</li>
                    <li>• Add multiple content sources for more diverse inspiration</li>
                    <li>• Customize your content style in Settings for consistent branding</li>
                    <li>• Use the approval workflow for team collaboration</li>
                </ul>
            </div>
        </div>
    );
};

const CompleteStep = ({ onComplete, credentials }) => {
    const hasOpenAI = credentials.OPENAI_API_KEY?.length > 0;
    const hasGemini = credentials.GEMINI_API_KEY?.length > 0;

    return (
        <div className="text-center space-y-6">
            <div className="mx-auto w-24 h-24 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircleIcon className="h-12 w-12 text-green-600" />
            </div>

            <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    🎉 Setup Complete!
                </h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                    You're all set up and ready to start creating amazing social media content with AI.
                </p>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Setup Summary:</h3>
                <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                        <span>Gemini API</span>
                        <span className={hasGemini ? 'text-green-600' : 'text-orange-500'}>
        {hasGemini ? '✅ Configured' : '⚠️ Recommended'}
    </span>
                    </div>
                    <div className="flex items-center justify-between">
                        <span>OpenAI API</span>
                        <span className={hasOpenAI ? 'text-green-600' : 'text-gray-400'}>
        {hasOpenAI ? '✅ Configured' : '⚪ Optional'}
    </span>
                    </div>
                    <div className="flex items-center justify-between">
                        <span>Social Platforms</span>
                        <span className="text-blue-600">🔧 Configure in Settings</span>
                    </div>
                </div>
            </div>

            <div className="space-y-3">
                <button
                    onClick={onComplete}
                    className="w-full inline-flex justify-center items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                    Start Using PostMgr
                    <RocketLaunchIcon className="ml-2 h-5 w-5" />
                </button>

                <p className="text-sm text-gray-500">
                    You can always return to Settings to add more platforms or update your API keys
                </p>
            </div>
        </div>
    );
};

// Quick Start Button Component (to trigger the tutorial)
const QuickStartButton = ({ onClick }) => {
    return (
        <button
            onClick={onClick}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 shadow-sm"
        >
            <RocketLaunchIcon className="h-4 w-4 mr-2" />
            Quick Start Tutorial
        </button>
    );
};

// Demo Component showing how to integrate
const OnboardingDemo = () => {
    const [showTutorial, setShowTutorial] = useState(false);
    const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);

    const handleComplete = () => {
        setShowTutorial(false);
        setHasCompletedOnboarding(true);
        // In real app: save to user preferences, redirect to dashboard, etc.
    };

    return (
        <div className="p-8 bg-gray-50 min-h-screen">
            <div className="max-w-4xl mx-auto">
                <div className="bg-white rounded-lg shadow-sm p-6">
                    <h1 className="text-2xl font-bold text-gray-900 mb-4">
                        PostMgr Dashboard
                    </h1>

                    {!hasCompletedOnboarding && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="font-medium text-blue-900">Welcome to PostMgr!</h3>
                                    <p className="text-sm text-blue-700 mt-1">
                                        Get started in just a few minutes with our quick setup tutorial
                                    </p>
                                </div>
                                <QuickStartButton onClick={() => setShowTutorial(true)} />
                            </div>
                        </div>
                    )}

                    <div className="text-center py-12">
                        <p className="text-gray-600">
                            {hasCompletedOnboarding
                                ? "Great! You've completed the setup. Start generating your first post!"
                                : "Click the Quick Start Tutorial to begin setting up your account."
                            }
                        </p>
                    </div>
                </div>
            </div>

            <OnboardingTutorial
                isOpen={showTutorial}
                onClose={() => setShowTutorial(false)}
                onComplete={handleComplete}
            />
        </div>
    );
};

export default OnboardingTutorial;